import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap, map, catchError, of } from 'rxjs';
import { ApiService } from './api.service';
import { StorageService } from './storage.service';
import type { AdminUser, AdminLoginResponse, TwoFactorSetupResponse } from '@everwhisper/shared-types';
import { getTimeUntilExpiration } from '../utils/jwt.utils';

@Injectable({
  providedIn: 'root',
})
export class AdminAuthService {
  private currentAdminSubject = new BehaviorSubject<AdminUser | null>(null);
  public currentAdmin$ = this.currentAdminSubject.asObservable();
  public isAdminAuthenticated$ = this.currentAdmin$.pipe(map(admin => !!admin));
  private refreshTimer: any = null;
  private isRefreshing = false;

  constructor(
    private api: ApiService,
    private storage: StorageService
  ) {
    // Initialize admin from storage and validate token
    this.initializeAuth();
  }

  /**
   * Initialize authentication state and set up auto-refresh
   */
  private initializeAuth(): void {
    const savedAdmin = this.storage.getAdmin();
    const token = this.storage.getAdminToken();

    if (savedAdmin && token) {
      // Check if token is expired
      if (this.storage.isAdminTokenExpired()) {
        console.log('Admin token expired on init, attempting refresh...');
        this.refreshToken().subscribe({
          next: () => {
            console.log('Admin token refreshed successfully on init');
            this.currentAdminSubject.next(savedAdmin);
            this.scheduleTokenRefresh();
          },
          error: () => {
            console.log('Admin token refresh failed on init, logging out');
            this.logout();
          }
        });
      } else {
        // Token is valid, set admin and schedule refresh
        this.currentAdminSubject.next(savedAdmin);
        this.scheduleTokenRefresh();
      }
    }
  }

  /**
   * Schedule automatic token refresh before expiration
   */
  private scheduleTokenRefresh(): void {
    // Clear any existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    const token = this.storage.getAdminToken();
    if (!token) {
      return;
    }

    // Get time until expiration and schedule refresh 1 hour before
    const timeUntilExpiration = getTimeUntilExpiration(token);
    const refreshBuffer = 3600; // 1 hour in seconds
    const refreshIn = Math.max(0, (timeUntilExpiration - refreshBuffer) * 1000);

    console.log(`Scheduling admin token refresh in ${Math.floor(refreshIn / 1000 / 60)} minutes`);

    this.refreshTimer = setTimeout(() => {
      console.log('Auto-refreshing admin token...');
      this.refreshToken().subscribe({
        next: () => {
          console.log('Admin token auto-refreshed successfully');
          this.scheduleTokenRefresh(); // Schedule next refresh
        },
        error: (error) => {
          console.error('Admin auto-refresh failed:', error);
        }
      });
    }, refreshIn);
  }

  login(email: string, password: string, twoFactorCode?: string): Observable<AdminLoginResponse> {
    return this.api.post<AdminLoginResponse>('/admin/auth/login', { 
      email, 
      password, 
      twoFactorCode 
    }).pipe(
      tap((response) => {
        if (!response.requiresTwoFactor) {
          this.storage.setAdminToken(response.token);
          this.storage.setAdmin(response.user);
          this.currentAdminSubject.next(response.user);
          this.scheduleTokenRefresh();
        }
      })
    );
  }

  /**
   * Login with Google OAuth
   */
  loginWithGoogle(idToken: string): Observable<AdminLoginResponse> {
    return this.api.post<AdminLoginResponse>('/admin/auth/google', { idToken }).pipe(
      tap((response) => {
        this.storage.setAdminToken(response.token);
        this.storage.setAdmin(response.user);
        this.currentAdminSubject.next(response.user);
        this.scheduleTokenRefresh();
      })
    );
  }

  /**
   * Refresh the admin authentication token
   */
  refreshToken(): Observable<AdminLoginResponse> {
    // Prevent multiple simultaneous refresh requests
    if (this.isRefreshing) {
      return of({} as AdminLoginResponse);
    }

    this.isRefreshing = true;

    return this.api.post<AdminLoginResponse>('/admin/auth/refresh', {}).pipe(
      tap((response) => {
        this.storage.setAdminToken(response.token);
        this.storage.setAdmin(response.user);
        this.currentAdminSubject.next(response.user);
        this.isRefreshing = false;
        this.scheduleTokenRefresh();
      }),
      catchError((error) => {
        this.isRefreshing = false;
        throw error;
      })
    );
  }

  setup2FA(password: string): Observable<TwoFactorSetupResponse> {
    return this.api.post<TwoFactorSetupResponse>('/admin/auth/setup-2fa', { password });
  }

  verify2FASetup(code: string): Observable<{ success: boolean; message: string }> {
    return this.api.post<{ success: boolean; message: string }>('/admin/auth/verify-2fa-setup', { code });
  }

  disable2FA(password: string): Observable<{ success: boolean; message: string }> {
    return this.api.post<{ success: boolean; message: string }>('/admin/auth/disable-2fa', { password });
  }

  logout(): void {
    // Clear refresh timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    
    this.api.post('/admin/auth/logout').subscribe();
    this.storage.clearAdmin();
    this.currentAdminSubject.next(null);
  }

  getAdminToken(): string | null {
    return this.storage.getAdminToken();
  }

  isAdminAuthenticated(): boolean {
    return !!this.storage.getAdminToken();
  }

  getCurrentAdmin(): Observable<AdminUser | null> {
    return this.currentAdmin$;
  }

  isAdmin(): boolean {
    const admin = this.currentAdminSubject.value;
    return !!admin && ['moderator', 'admin', 'super_admin'].includes(admin.role);
  }

  isSuperAdmin(): boolean {
    const admin = this.currentAdminSubject.value;
    return !!admin && admin.role === 'super_admin';
  }

  hasRole(role: 'moderator' | 'admin' | 'super_admin'): boolean {
    const admin = this.currentAdminSubject.value;
    if (!admin) return false;
    
    const roleHierarchy = { moderator: 1, admin: 2, super_admin: 3 };
    const userRoleLevel = roleHierarchy[admin.role as keyof typeof roleHierarchy] || 0;
    const requiredRoleLevel = roleHierarchy[role];
    
    return userRoleLevel >= requiredRoleLevel;
  }
}
